// Incoming Text Review (approved HTML:
// Incoming-Text-Consistency-Review-v5.html).
//
// Text and email cannot inherit the other channel's recipient, subject,
// draft, notes or request. Save and Return retains draft, scroll and
// call state. Send never implicitly finishes. Writes gate on
// LIVE_WRITE_GATE_KIXIE_WEBHOOK_INBOUND.
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { BoundaryState } from '../_shared/DataBoundary.js';
import { IncomingShell } from '../IncomingEmail/components/IncomingShell.js';
import { INCOMING_TEXT } from './mockData.js';
import { useScreenRuntime } from '../CurrentActionReview/runtime-clients.js';
import './styles.css';

export interface IncomingTextScreenProps {
  stateOverride?: BoundaryState;
}

export function IncomingTextScreen({
  stateOverride,
}: IncomingTextScreenProps = {}): ReactNode {
  // W2A upper lane: mirror of Incoming Email. Probes the Kixie webhook
  // gate when a signed session is live. Explicit stateOverride wins.
  const runtime = useScreenRuntime();
  const { customerId = '' } = useParams();
  const [liveState, setLiveState] = useState<BoundaryState | null>(null);
  useEffect(() => {
    if (!runtime || stateOverride || !customerId) { setLiveState(null); return; }
    let cancelled = false;
    setLiveState({ kind: 'loading', label: 'Loading inbound text…' });
    runtime.work
      .readGate('LIVE_WRITE_GATE_KIXIE_WEBHOOK_INBOUND')
      .then((gate) => {
        if (cancelled) return;
        if (!gate.on) {
          setLiveState({
            kind: 'blocked',
            title: 'Incoming text reply is paused',
            reason: gate.reason ?? 'Live-write gate OFF for Kixie inbound webhook.',
            gateName: 'LIVE_WRITE_GATE_KIXIE_WEBHOOK_INBOUND',
          });
          return;
        }
        setLiveState(null);
      })
      .catch(() => {
        if (cancelled) return;
        // W38 (2026-10-20): same rule as Incoming Email. The gate route is
        // bearer-only (401 INVALID_BEARER on url-bootstrap, MEASURED) and a
        // 404 means it is unmounted; any read failure is gate OFF. The real
        // body stays and Send Reply is inert ("Paused by the live-write
        // gate") instead of an error card over the contact.
        setLiveState(null);
      });
    return () => { cancelled = true; };
  }, [runtime, stateOverride, customerId]);

  return (
    <IncomingShell
      variant="text"
      mock={INCOMING_TEXT}
      blockedGate="LIVE_WRITE_GATE_KIXIE_WEBHOOK_INBOUND"
      blockedTitle="Incoming text reply is paused"
      blockedReason="Live-write gate OFF for Kixie inbound webhook."
      testId="incoming-text-boundary"
      stateOverride={stateOverride ?? liveState ?? undefined}
    />
  );
}

export default IncomingTextScreen;
